import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { Visibility as VisibilityIcon, VisibilityOff as VisibilityOffIcon } from '@mui/icons-material';

export interface EvoCredentialsValues {
  dns: string;
  token: string;
  nativeIntegration: boolean;
}

export interface EvoCredentialsSubmitResult {
  success: boolean;
  error?: string;
  conflictingWebhookConfigId?: string;
}

interface Props {
  open: boolean;
  /** Credenciais salvas (edição) ou `null` (cadastro). */
  initial: EvoCredentialsValues | null;
  /** ID do cadastro aberto: o "Abrir cadastro" do conflito só aparece para outro cadastro. */
  currentConfigId: string;
  onClose: () => void;
  /** Salva as credenciais. O pai fecha o diálogo em caso de sucesso. */
  onSubmit: (values: EvoCredentialsValues) => Promise<EvoCredentialsSubmitResult>;
  onOpenConflict: (configId: string) => void;
}

interface SubmitError {
  message: string;
  conflictingWebhookConfigId?: string;
  /** Valores enviados quando o erro voltou (o conflito só some se DNS ou token mudarem). */
  dns: string;
  token: string;
}

const NATIVE_HELP_OFF = 'Marque se este token foi gerado pela automação da EVO.';
const NATIVE_HELP_ON = 'As chamadas à EVO com este token levarão o cabeçalho da integração nativa.';
const NATIVE_HELP_RULE = 'Você pode corrigir esta opção sem trocar o token. Se a EVO confirmar webhooks Reedy ativos, remova-os antes. Se a consulta falhar ou retornar zero webhooks Reedy, a edição continua disponível.';

/** Diálogo de credenciais da conta EVO: DNS, token e tipo do token (REEDY-114, D1 e D5). */
const EvoWebhookCredentialsDialog: React.FC<Props> = ({ open, initial, currentConfigId, onClose, onSubmit, onOpenConflict }) => {
  const [dns, setDns] = useState('');
  const [token, setToken] = useState('');
  const [nativeIntegration, setNativeIntegration] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<SubmitError | null>(null);

  useEffect(() => {
    if (!open) return;
    setDns(initial?.dns ?? '');
    setToken(initial?.token ?? '');
    setNativeIntegration(initial?.nativeIntegration ?? false);
    setShowToken(false);
    setSaving(false);
    setError(null);
    // Só reinicia ao abrir: o pai atualiza `initial` depois de salvar, com o diálogo fechando.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const isEdit = initial !== null;
  const tokenChanged = isEdit && token.trim() !== initial.token.trim();
  const isConflict = Boolean(error?.conflictingWebhookConfigId);
  const conflictBlocked = isConflict && dns.trim() === error?.dns && token.trim() === error?.token;
  const saveDisabled = saving || !dns.trim() || !token.trim() || conflictBlocked;

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const handleSave = async () => {
    const values: EvoCredentialsValues = { dns: dns.trim(), token: token.trim(), nativeIntegration };
    setSaving(true);
    setError(null);
    try {
      const result = await onSubmit(values);
      if (!result.success) {
        setError({
          message: result.error || 'Erro ao salvar credenciais',
          conflictingWebhookConfigId: result.conflictingWebhookConfigId,
          dns: values.dns,
          token: values.token,
        });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth aria-labelledby="evo-credentials-title">
      <DialogTitle id="evo-credentials-title">{isEdit ? 'Editar credenciais' : 'Cadastrar credenciais'}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {error && (
            <Alert
              severity="error"
              action={
                error.conflictingWebhookConfigId && error.conflictingWebhookConfigId !== currentConfigId ? (
                  <Button color="inherit" size="small" onClick={() => onOpenConflict(error.conflictingWebhookConfigId as string)}>
                    Abrir cadastro
                  </Button>
                ) : undefined
              }
            >
              {error.message}
            </Alert>
          )}
          <TextField
            fullWidth
            required
            label="DNS"
            value={dns}
            onChange={(e) => setDns(e.target.value)}
            disabled={saving}
          />
          <TextField
            fullWidth
            required
            label="Token"
            type={showToken ? 'text' : 'password'}
            value={token}
            onChange={(e) => setToken(e.target.value)}
            disabled={saving}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    aria-label={showToken ? 'Ocultar token' : 'Mostrar token'}
                    onClick={() => setShowToken((visible) => !visible)}
                    disabled={saving}
                  >
                    {showToken ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          {tokenChanged && (
            <Alert severity="warning" role="status">
              Você alterou o token. Confirme se o novo token é de integração nativa.
            </Alert>
          )}
          <FormControl disabled={saving}>
            <FormControlLabel
              control={<Checkbox checked={nativeIntegration} onChange={(e) => setNativeIntegration(e.target.checked)} />}
              label="Token de integração nativa"
            />
            <FormHelperText sx={{ mt: 0, ml: 4 }}>{nativeIntegration ? NATIVE_HELP_ON : NATIVE_HELP_OFF} {NATIVE_HELP_RULE}</FormHelperText>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap' }}>
        {conflictBlocked && (
          <Typography variant="caption" color="text.secondary" sx={{ flex: '1 1 200px', pl: 1 }}>
            Altere o DNS ou o token para poder salvar.
          </Typography>
        )}
        <Button onClick={handleClose} disabled={saving}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saveDisabled}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {saving ? 'Verificando…' : 'Salvar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EvoWebhookCredentialsDialog;
